import React, { useMemo, useState } from 'react';
import {
	Box,
	Button,
	Card,
	Chip,
	Collapse,
	Divider,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { Ticket, TicketAttachment } from '../../types/ticket';
import { ParkingLocation } from '../../types/parkingLocation';
import { stateProvinceList } from '../../data/stateProvinceList';
import { mapTicketTypeToColor } from '../../utils/mapTicketTypeToColor';
import { useTicketTypeMeta } from '../../context/TicketTerminologyProvider';
import { getAttachmentPreviewUrl } from '../../utils/getAttachmentPreviewUrl';
import AttachmentImageGallery from '../attachmentImageGallery/AttachmentImageGallery';
import TicketAttachments from '../ticketAttachments/ticketAttachments';

interface Props {
	ticket: Ticket;
	parkingLocation: ParkingLocation;
	onViewLicenseHistory: (ticket: Ticket) => void;
}

const isImage = (att: TicketAttachment) =>
	att.ContentType?.toLowerCase().includes('image');

interface FieldProps {
	label: string;
	value: React.ReactNode;
	muted?: boolean;
}

const Field: React.FC<FieldProps> = ({ label, value, muted }) => (
	<Stack spacing={0.25} sx={{ minWidth: 0 }}>
		<Typography
			variant="overline"
			color="text.secondary"
			sx={{ lineHeight: 1.2, letterSpacing: '0.08em' }}
		>
			{label}
		</Typography>
		<Typography
			variant="body2"
			fontWeight={300}
			color={muted ? 'text.secondary' : 'text.primary'}
			sx={{ wordBreak: 'break-word' }}
		>
			{value}
		</Typography>
	</Stack>
);

const SectionGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<Box
		sx={{
			display: 'grid',
			gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
			columnGap: 2,
			rowGap: 1.25,
		}}
	>
		{children}
	</Box>
);

const LotHistoryTicketCard: React.FC<Props> = ({
	ticket,
	parkingLocation,
	onViewLicenseHistory,
}) => {
	const [expanded, setExpanded] = useState(false);

	const {
		ticketNumber,
		ticketType,
		licenseNumber,
		licenseState,
		parkingZone,
		date,
		vehicleInfo,
		issuedBy,
		comments,
		attachments,
		isVoid,
	} = ticket;

	const images = useMemo(
		() => (attachments ?? []).filter(isImage),
		[attachments],
	);
	const hasCover = images.length > 0;
	const coverImage = images[0];
	const moreImages = images.length - 1;

	const stateObj = stateProvinceList.find((s) => s.code === licenseState);
	const licenseStateLabel = stateObj ? stateObj.label : licenseState;

	const isoDate = new Date(date);
	const fullDate = format(isoDate, 'EEEE, do MMMM yyyy, h:mm a');
	const relativeDate = `${formatDistanceToNowStrict(isoDate)} ago`;

	const getTicketTypeMeta = useTicketTypeMeta();
	const { shortLabel, hexColor } = getTicketTypeMeta(ticketType);
	const chipColor = mapTicketTypeToColor(ticketType);

	const isExpiryLot =
		parkingLocation?.violationCheckType === 'hourlyExpiry' &&
		!!parkingLocation?.maxStayMinutes;
	const isExpired =
		isExpiryLot &&
		Date.now() >=
			isoDate.getTime() + (parkingLocation.maxStayMinutes ?? 0) * 60_000;

	const plateLine = `${licenseNumber} (${licenseStateLabel})`;
	const locationLine = `${parkingLocation?.name}${
		parkingZone ? ` — ${parkingZone}` : ''
	}`;
	const hasComments = !!comments && comments.trim().length > 0;
	const hasVehicleInfo = !!vehicleInfo && vehicleInfo.trim().length > 0;
	const hasAttachments = (attachments ?? []).length > 0;
	const nonImageAttachments = (attachments ?? []).filter((a) => !isImage(a));

	return (
		<Card
			variant="outlined"
			sx={{
				width: '100%',
				borderRadius: 2,
				overflow: 'hidden',
				borderTop: hasCover ? undefined : `4px solid ${hexColor}`,
				opacity: isVoid ? 0.85 : 1,
			}}
		>
			{hasCover && (
				<Box
					onClick={() => setExpanded((v) => !v)}
					sx={{
						position: 'relative',
						width: '100%',
						aspectRatio: '16 / 9',
						overflow: 'hidden',
						bgcolor: 'background.default',
						cursor: 'pointer',
					}}
				>
					<Box
						component="img"
						src={getAttachmentPreviewUrl(coverImage.AttachmentID)}
						alt={coverImage.Name}
						sx={{
							width: '100%',
							height: '100%',
							objectFit: 'cover',
							display: 'block',
						}}
					/>
					{/* Top gradient for chip legibility */}
					<Box
						sx={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							height: 64,
							background:
								'linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0))',
							pointerEvents: 'none',
						}}
					/>
					<Stack
						direction="row"
						spacing={0.5}
						sx={{ position: 'absolute', top: 8, left: 8 }}
					>
						<Chip
							label={shortLabel}
							color={chipColor}
							size="small"
							sx={{ fontWeight: 500 }}
						/>
					</Stack>
					{isVoid && (
						<Chip
							label="Void"
							size="small"
							sx={{
								position: 'absolute',
								top: 8,
								right: 8,
								bgcolor: 'rgba(0,0,0,0.6)',
								color: 'common.white',
								fontWeight: 500,
							}}
						/>
					)}
					{moreImages > 0 && (
						<Chip
							icon={
								<AttachFileIcon
									style={{ color: 'inherit' }}
									fontSize="small"
								/>
							}
							label={`+${moreImages}`}
							size="small"
							sx={{
								position: 'absolute',
								bottom: 8,
								right: 8,
								bgcolor: 'rgba(0,0,0,0.6)',
								color: 'common.white',
								'& .MuiChip-icon': { color: 'common.white' },
							}}
						/>
					)}
				</Box>
			)}

			<Box
				role="button"
				aria-expanded={expanded}
				onClick={() => setExpanded((v) => !v)}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					p: 1.25,
					cursor: 'pointer',
					'&:hover': { backgroundColor: 'action.hover' },
				}}
			>
				<Box sx={{ flexGrow: 1, minWidth: 0 }}>
					<Stack
						direction="row"
						alignItems="center"
						spacing={1}
						sx={{ flexWrap: 'wrap' }}
					>
						{!hasCover && (
							<Chip
								label={shortLabel}
								color={chipColor}
								size="small"
								sx={{ fontWeight: 500 }}
							/>
						)}
						<Typography
							variant="body1"
							fontWeight={500}
							sx={{
								fontFamily:
									'ui-monospace, SFMono-Regular, Menlo, monospace',
								letterSpacing: '0.04em',
								textDecoration: isVoid ? 'line-through' : 'none',
							}}
						>
							{licenseNumber}
						</Typography>
						<Chip
							label={licenseState}
							size="small"
							variant="outlined"
							sx={{ height: 20 }}
						/>
						{isExpiryLot && (
							<Chip
								label={isExpired ? 'Expired' : 'Active'}
								size="small"
								color={isExpired ? 'error' : 'success'}
								variant="outlined"
								sx={{ height: 20 }}
							/>
						)}
						{!hasCover && isVoid && (
							<Chip
								label="Void"
								size="small"
								color="default"
								variant="outlined"
							/>
						)}
					</Stack>
					<Typography
						variant="caption"
						color="text.secondary"
						fontWeight={300}
						sx={{
							display: 'block',
							mt: 0.25,
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						}}
					>
						{relativeDate}
						{parkingZone ? ` · ${parkingZone}` : ''} ·{' '}
						{hasVehicleInfo ? vehicleInfo : 'No vehicle info'} ·{' '}
						{issuedBy || 'Unknown issuer'}
					</Typography>
				</Box>
				<Stack direction="row" alignItems="center" spacing={0.5}>
					{hasAttachments && (
						<Chip
							icon={<AttachFileIcon fontSize="small" />}
							label={attachments.length}
							size="small"
							variant="outlined"
						/>
					)}
					<IconButton
						size="small"
						aria-label={expanded ? 'Collapse' : 'Expand'}
						onClick={(e) => {
							e.stopPropagation();
							setExpanded((v) => !v);
						}}
					>
						{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
				</Stack>
			</Box>

			<Collapse in={expanded} unmountOnExit>
				<Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
					{isVoid && (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								p: 1,
								mb: 1.5,
								borderRadius: 1,
								backgroundColor: 'action.hover',
								color: 'text.secondary',
							}}
						>
							<WarningOutlinedIcon fontSize="small" />
							<Typography variant="body2" fontWeight={400}>
								This record has been voided
							</Typography>
						</Box>
					)}

					<Stack spacing={1.5} divider={<Divider flexItem />}>
						<SectionGrid>
							<Field label="Date" value={fullDate} />
							<Field label="Record #" value={ticketNumber} />
							<Field label="Location" value={locationLine} />
						</SectionGrid>

						<SectionGrid>
							<Field label="License Plate" value={plateLine} />
							<Field
								label="Vehicle"
								value={
									hasVehicleInfo ? vehicleInfo : 'Not provided'
								}
								muted={!hasVehicleInfo}
							/>
						</SectionGrid>

						<Stack spacing={1.25}>
							<Field
								label="Issued By"
								value={issuedBy || 'Unknown'}
								muted={!issuedBy}
							/>
							{hasComments && (
								<Field label="Comments" value={comments} />
							)}
						</Stack>

						{hasAttachments && (
							<Stack spacing={1}>
								<Typography
									variant="overline"
									color="text.secondary"
									sx={{
										lineHeight: 1.2,
										letterSpacing: '0.08em',
									}}
								>
									Attachments
								</Typography>
								{images.length > 0 && (
									<AttachmentImageGallery
										attachments={attachments}
									/>
								)}
								{nonImageAttachments.length > 0 && (
									<TicketAttachments
										attachments={nonImageAttachments}
										disableDelete={true}
									/>
								)}
							</Stack>
						)}
					</Stack>
				</Box>
			</Collapse>
		</Card>
	);
};

export default LotHistoryTicketCard;
